'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { CrossDark } from 'assets/icons';
import CustomLink from 'components/shared/CustomLink';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { classifyRoute } from 'lib/consent/policy';
import styles from 'components/shared/CookieConsentBanner.module.css';
import { useConsentRuntime } from './ConsentRuntime';
import { COOKIE_POLICY_URL } from './PrivacyChoicesDialog';

const HEIGHT_VAR = '--cookie-banner-height';

export function NextStackBanner({ className }: { className?: string }) {
  const t = useTranslations('common');
  const pathname = useInternalPathname();
  const {
    stack,
    choice,
    consentKnown,
    gpc,
    setBannerReady,
    choose,
    openPrivacyChoices,
  } = useConsentRuntime();
  const [closed, setClosed] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const headingId = useId();
  const page = classifyRoute(pathname);
  const show =
    stack === 'next' &&
    consentKnown &&
    choice === 'none' &&
    page !== 'private' &&
    !closed;

  useEffect(() => {
    if (show) setBannerReady(true);
  }, [show, setBannerReady]);

  // Feeds scroll-padding-bottom so focused elements scroll clear of the banner.
  useEffect(() => {
    const node = rootRef.current;
    if (!show || !node) return;
    const html = document.documentElement;
    const update = () =>
      html.style.setProperty(HEIGHT_VAR, `${node.offsetHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => {
      observer.disconnect();
      html.style.removeProperty(HEIGHT_VAR);
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      ref={rootRef}
      className={className ? `${styles.root} ${className}` : styles.root}
      role="region"
      aria-labelledby={headingId}
      data-cookie-banner
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 id={headingId} className={styles.heading}>
            {t('cookieBanner.heading')}
          </h2>
          <button
            className={styles.closeButton}
            type="button"
            aria-label={t('cookieBanner.closeNoChoice')}
            onClick={() => setClosed(true)}
          >
            <CrossDark fill="#ffffff" />
          </button>
        </div>
        <p className={styles.description}>
          {t('cookieBanner.description')}
          <CustomLink
            className={styles.policyLink}
            href={COOKIE_POLICY_URL}
            target="_blank"
            rel="noreferrer"
          >
            {t('cookieBanner.policyLink')}
          </CustomLink>
        </p>
        {gpc ? (
          <p className={styles.gpcStatus}>{t('cookieBanner.gpcHonored')}</p>
        ) : null}
        <div className={styles.actions}>
          <button
            className={`${styles.actionButton} ${styles.primary}`}
            type="button"
            data-consent-action="accept"
            onClick={() => choose('accept')}
          >
            {t('cookieBanner.ACCEPT')}
          </button>
          <button
            className={`${styles.actionButton} ${styles.outline}`}
            type="button"
            data-decline="equal"
            onClick={() => choose('reject')}
          >
            {t('cookieBanner.REJECT')}
          </button>
          <button
            className={styles.manageButton}
            type="button"
            data-consent-action="manage"
            onClick={openPrivacyChoices}
          >
            {t('cookieBanner.manage')}
          </button>
        </div>
      </div>
    </div>
  );
}
